import { useState, useMemo, useEffect } from 'react'

import { Modal, Input, Text, Tooltip } from '@mantine/core'

import { useMutation } from '@redwoodjs/web'

import DNButton from 'src/components/Buttons/Button/DNButton'
import { toast } from 'src/components/Overlays/Toast/Toast'
import { UPDATE_APPLICANT_FILTER_PRESET } from 'src/graphql/applicantFilterPresets.graphql'

const EditViewModal = ({
  opened,
  onClose,
  preset,
  refetch,
  existingPresetNames = [],
}) => {
  const [viewName, setViewName] = useState('')

  // Initialize viewName when preset changes
  useEffect(() => {
    if (preset) {
      setViewName(preset.name || '')
    }
  }, [preset])

  const [updateFilterPreset, { loading: updateFilterPresetLoading }] =
    useMutation(UPDATE_APPLICANT_FILTER_PRESET, {
      onCompleted: () => {
        onClose(viewName, false)
        refetch?.()
        toast('View renamed', 'success')
      },
      onError: (error) => {
        console.error('Error updating preset:', error?.message)
        toast('Failed to rename view', 'error')
      },
    })

  // Reset form when modal closes
  const handleClose = () => {
    setViewName('')
    onClose(null, false)
  }

  // Check for duplicate names (excluding the current preset name)
  const isDuplicateName = useMemo(() => {
    if (!viewName.trim()) return false
    if (viewName.trim().toLowerCase() === preset?.name?.toLowerCase())
      return false // Same name (case-insensitive) is allowed
    return existingPresetNames.some(
      (existingName) =>
        existingName.toLowerCase() === viewName.trim().toLowerCase()
    )
  }, [viewName, existingPresetNames, preset])

  // Check if form is valid
  const isFormValid = viewName.trim().length > 0 && !isDuplicateName
  const isSubmitDisabled = !isFormValid || updateFilterPresetLoading

  const handleUpdateView = async () => {
    if (!preset?.applicantFilterPresetId) return

    await updateFilterPreset({
      variables: {
        applicantFilterPresetId: preset.applicantFilterPresetId,
        input: {
          name: viewName.trim(),
        },
      },
    })
  }

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      if (isSubmitDisabled) return
      handleUpdateView()
    }
  }

  return (
    <Modal
      opened={opened}
      withCloseButton={false}
      onClose={handleClose}
      closeOnClickOutside={true}
      closeOnEscape={true}
      yOffset={100}
      overlayProps={{
        backgroundOpacity: 0.2,
        blur: 5,
      }}
      classNames={{
        body: 'flex flex-col justify-between items-start',
      }}
    >
      <Text className="text-sm font-medium text-doubleNickel-gray-500">
        View name:
      </Text>
      <Input
        placeholder="Enter view name (max 30 chars)"
        variant="unstyled"
        className="w-full"
        onKeyDown={handleKeyDown}
        value={viewName}
        onChange={(event) => setViewName(event.target.value)}
        maxLength={30}
      />
      <div className="mt-4 flex w-full flex-row items-center justify-end gap-2">
        <DNButton variant="outline" className="h-7" onClick={handleClose}>
          Cancel
        </DNButton>

        <Tooltip
          label={
            isDuplicateName
              ? 'A view with this name already exists'
              : viewName.trim().length === 0
              ? 'Please enter a view name'
              : null
          }
          disabled={!isSubmitDisabled}
          position="top"
          withArrow
        >
          <DNButton
            variant="primary"
            className="h-7 min-w-20"
            disabled={isSubmitDisabled}
            loading={updateFilterPresetLoading}
            onClick={handleUpdateView}
          >
            Save
          </DNButton>
        </Tooltip>
      </div>
    </Modal>
  )
}

export default EditViewModal
